import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import AdminTable from '../../components/Admin/AdminTable';
import ExcelUploadModal from '../../components/Admin/ExcelUploadModal';

const SAMPLE_COLS = ['room_id','device_name','oem','model_part','serial_number','installation_date'];
const STATUSES = ['active','replaced','faulty'];
const EMPTY_FORM = { room:'', device_name:'', oem:'', model_part:'', serial_number:'', status:'active', installation_date:'' };

const TABLE_COLS = [
  { key:'device_name', label:'Device Name' },
  { key:'oem', label:'OEM' },
  { key:'model_part', label:'Model/Part' },
  { key:'serial_number', label:'Serial No' },
  { key:'room_name', label:'Room' },
  { key:'location_name', label:'Location' },
  { key:'status', label:'Status', render: v => {
    const c = { active:'bg-green-100 text-green-700', replaced:'bg-blue-100 text-blue-700', faulty:'bg-red-100 text-red-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span>;
  }},
];

export default function Devices() {
  const [data, setData] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [oemFilter, setOemFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');

  const locationOptions = [...new Set(data.map(d => d.location_name).filter(Boolean))].sort();
  const oemOptions = [...new Set(data.map(d => d.oem).filter(Boolean))].sort();
  const roomOptions = [...new Set(data.map(d => d.room_name).filter(Boolean))].sort();

  const filteredData = data.filter(d => {
    if (locationFilter && d.location_name !== locationFilter) return false;
    if (oemFilter && d.oem !== oemFilter) return false;
    if (statusFilter && d.status !== statusFilter) return false;
    if (roomFilter && d.room_name !== roomFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${d.device_name || ''} ${d.model_part || ''} ${d.serial_number || ''} ${d.room_name || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devs, rms] = await Promise.all([
        client.get('/admin/devices/'),
        client.get('/admin/rooms/'),
      ]);
      setData(Array.isArray(devs.data) ? devs.data : devs.data.results ?? []);
      setRooms(Array.isArray(rms.data) ? rms.data : rms.data.results ?? []);
    } catch { setError('Failed to load devices.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditRow(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row) => { setEditRow(row); setForm({ ...row, room: row.room ?? '', installation_date: row.installation_date || '' }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editRow) await client.put(`/admin/devices/${editRow.id}/`, form);
      else await client.post('/admin/devices/', form);
      setShowForm(false); load();
    } catch (err) { alert(err?.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await client.delete(`/admin/devices/${deleteRow.id}/`); setDeleteRow(null); load(); }
    catch { alert('Delete failed.'); }
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data: res } = await client.post('/admin/upload/devices/', fd);
    load(); return res;
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-purple-300';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Room Inventory</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage devices installed in rooms</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Upload Excel
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Device
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Search device, model, serial, room…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-purple-300"
        />
        <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Locations</option>
          {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={roomFilter} onChange={e => setRoomFilter(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Rooms</option>
          {roomOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={oemFilter} onChange={e => setOemFilter(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All OEMs</option>
          {oemOptions.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        {(search || locationFilter || oemFilter || statusFilter || roomFilter) && (
          <button
            onClick={() => { setSearch(''); setLocationFilter(''); setOemFilter(''); setStatusFilter(''); setRoomFilter(''); }}
            className="px-3 py-2 text-sm rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            Clear
          </button>
        )}
        <span className="flex items-center text-xs text-gray-400 px-2">
          {filteredData.length} of {data.length} devices
        </span>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <AdminTable columns={TABLE_COLS} data={filteredData} loading={loading} onEdit={openEdit} onDelete={setDeleteRow} />
      </div>

      {showUpload && <ExcelUploadModal title="Devices" onUpload={handleUpload} onClose={() => setShowUpload(false)} sampleColumns={SAMPLE_COLS} templateEntity="devices" />}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{editRow ? 'Edit Device' : 'Add Device'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="text-xs font-medium text-gray-600 mb-1 block">Room *</label>
                <select className={inp} value={form.room} onChange={e => set('room', e.target.value)} required>
                  <option value="">Select roomâ€¦</option>
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.room_name} | {r.location_name}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Device Name *</label><input className={inp} value={form.device_name} onChange={e => set('device_name', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">OEM</label><input className={inp} value={form.oem} onChange={e => set('oem', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Model / Part</label><input className={inp} value={form.model_part} onChange={e => set('model_part', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Serial Number</label><input className={inp} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
                <select className={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Installation Date</label><input type="date" className={inp} value={form.installation_date} onChange={e => set('installation_date', e.target.value)} /></div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">{saving ? 'Savingâ€¦' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Delete Device</h3>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteRow.device_name}</strong>?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteRow(null)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

